/**
 * Shared candidate pipeline action catalog + transition rules.
 * Presentation (Table / Meta / Kanban) only decides layout; move policy mirrors
 * backend CandidatePipelineService.
 */
import type { Candidate, PipelineStage } from '../models/job';
import {
  createStageResolver,
  sortStagesByPosition,
} from './pipeline-stages';

export type CandidatePipelineActionId =
  | 'send_interview'
  | 'accept'
  | 'reject'
  | 'hire';

export type CandidateContextualActionId =
  | 'download'
  | 'delete'
  | 'extend'
  | 'shortlist';

export interface CandidateActionDescriptor {
  id: CandidatePipelineActionId;
}

export interface CandidateContextualDescriptor {
  id: CandidateContextualActionId;
}

export interface CandidatePipelineActions {
  primary: CandidateActionDescriptor | null;
  /**
   * Contextual icons shown before pipeline actions (e.g. Extend for Interviewing,
   * Shortlist for Pending).
   */
  leading: CandidateContextualDescriptor[];
  /** Pipeline actions shown inline next to the primary (Approve / Decline / Hire). */
  direct: CandidateActionDescriptor[];
  /** Contextual actions that belong in the `...` menu. */
  contextual: CandidateContextualDescriptor[];
}

const PIPELINE_ORDER: CandidatePipelineActionId[] = [
  'send_interview',
  'accept',
  'reject',
  'hire',
];

const NO_DELETE_SEMANTICS = new Set(['accepted', 'rejected', 'hired']);

/** Delete is available on every non-terminal stage, including custom. */
export function canDeleteCandidateInStage(
  semantic: string | undefined
): boolean {
  return !semantic || !NO_DELETE_SEMANTICS.has(semantic);
}

function menuContextualForSemantic(
  semantic: string | undefined
): CandidateContextualDescriptor[] {
  const items: CandidateContextualDescriptor[] = [{ id: 'download' }];

  if (canDeleteCandidateInStage(semantic)) {
    items.push({ id: 'delete' });
  }

  return items;
}

function canExtendInterview(
  candidate: Pick<Candidate, 'interviewId' | 'interviewExpiresAt'>
): boolean {
  return Boolean(candidate.interviewId && candidate.interviewExpiresAt);
}

/**
 * Returns the pipeline primary + direct actions and contextual companions
 * for a candidate's stage semantic. Does not invent transitions the backend rejects.
 */
export function getCandidatePipelineActions(
  candidate: Pick<
    Candidate,
    'pipelineStageSemantic' | 'interviewId' | 'interviewExpiresAt'
  >
): CandidatePipelineActions {
  const semantic = candidate.pipelineStageSemantic;
  const contextual = menuContextualForSemantic(semantic);
  const leading: CandidateContextualDescriptor[] =
    semantic === 'interviewing' && canExtendInterview(candidate)
      ? [{ id: 'extend' }]
      : [];

  switch (semantic) {
    case 'pending':
    case 'shortlisted':
      return {
        primary: { id: 'send_interview' },
        leading: semantic === 'pending' ? [{ id: 'shortlist' }] : [],
        direct: [{ id: 'accept' }, { id: 'reject' }],
        contextual,
      };
    case 'interviewing':
      return {
        primary: { id: 'accept' },
        leading,
        direct: [{ id: 'reject' }],
        contextual,
      };
    case 'interviewed':
      return {
        primary: { id: 'accept' },
        leading: [],
        direct: [{ id: 'reject' }, { id: 'hire' }],
        contextual,
      };
    case 'accepted':
      return {
        primary: { id: 'hire' },
        leading: [],
        direct: [{ id: 'reject' }],
        contextual,
      };
    case 'rejected':
    case 'hired':
      return {
        primary: null,
        leading: [],
        direct: [],
        contextual,
      };
    default:
      return { primary: null, leading: [], direct: [], contextual };
  }
}

/** All pipeline action ids available for a candidate (primary + direct). */
export function listPipelineActionIds(
  actions: CandidatePipelineActions
): CandidatePipelineActionId[] {
  const ids: CandidatePipelineActionId[] = [];
  if (actions.primary) ids.push(actions.primary.id);
  for (const item of actions.direct) ids.push(item.id);
  return PIPELINE_ORDER.filter((id) => ids.includes(id));
}

/**
 * Soft client mirror of backend move policy, plus UI-only rules:
 * - same stage → blocked (reorder is handled separately)
 * - interviewed → interviewing → blocked (backend also rejects this pair)
 * - interviewed → any earlier column (lower position) → blocked
 * - any drop *into* interviewed → blocked (that stage is set when the
 *   interview completes, not by dragging)
 * - otherwise allowed (including out of hired/rejected; custom stages OK)
 *
 * Backend remains the source of truth for API moves; this only avoids
 * obvious bad drops on the board.
 */
export function canMoveCandidateToStage(
  fromStageType: string | null | undefined,
  toStageType: string | null | undefined,
  options: {
    sameStageId: boolean;
    fromPosition?: number;
    toPosition?: number;
  }
): boolean {
  if (options.sameStageId) return false;
  if (toStageType === 'interviewed') return false;
  if (fromStageType === 'interviewed' && toStageType === 'interviewing') {
    return false;
  }
  if (
    fromStageType === 'interviewed' &&
    options.fromPosition != null &&
    options.toPosition != null &&
    options.toPosition < options.fromPosition
  ) {
    return false;
  }
  return true;
}

export interface StageMoveTargets {
  /** Position of the candidate's current stage, for the move rules. */
  fromPosition?: number;
  /** Every other stage in board order; blocked ones stay listed but disallowed. */
  targets: Array<{ stage: PipelineStage; isAllowed: boolean }>;
}

/** Destination stages for a "Move to" action, using the same rules as a Kanban drop. */
export function getStageMoveTargets(
  candidate: Candidate,
  stages: PipelineStage[]
): StageMoveTargets {
  const ordered = sortStagesByPosition(stages);
  const currentStageId = createStageResolver(ordered)(candidate);
  const fromPosition = ordered.find(
    (stage) => stage.id === currentStageId
  )?.position;

  return {
    fromPosition,
    targets: ordered
      .filter((stage) => stage.id !== currentStageId)
      .map((stage) => ({
        stage,
        isAllowed: canMoveCandidateToStage(
          candidate.pipelineStageSemantic,
          stage.stageType,
          {
            sameStageId: false,
            fromPosition,
            toPosition: stage.position,
          }
        ),
      })),
  };
}

/** Shortlist action target: the Shortlisted stage, only when the catalog offers Shortlist. */
export function getShortlistStage(
  actions: CandidatePipelineActions,
  stages: PipelineStage[] | undefined
): PipelineStage | undefined {
  if (!actions.leading.some((item) => item.id === 'shortlist')) return undefined;
  return stages?.find((stage) => stage.stageType === 'shortlisted');
}

/** Meta CV footer: shown for pipeline actions or when Move To has an allowed stage. */
export function hasCandidateFooterActions(
  candidate: Candidate,
  stages: PipelineStage[] | undefined
): boolean {
  if (listPipelineActionIds(getCandidatePipelineActions(candidate)).length > 0) {
    return true;
  }
  if (!stages) return false;
  return getStageMoveTargets(candidate, stages).targets.some(
    (target) => target.isAllowed
  );
}

export type DragMappedAction =
  | { kind: 'send_interview' }
  | { kind: 'accept' }
  | { kind: 'reject' }
  | { kind: 'hire' }
  | { kind: 'pipeline_move' }
  | { kind: 'invalid' };

/**
 * Map a Kanban drop onto either a confirmation-backed shortcut (interview /
 * accept / reject / hire) or a direct pipeline-moves call.
 */
export function mapDragToPipelineAction(
  candidate: Pick<Candidate, 'pipelineStageSemantic' | 'pipelineStageId'>,
  targetStage: { id: string; stageType: string; position?: number },
  fromPosition?: number
): DragMappedAction {
  const fromType = candidate.pipelineStageSemantic;
  if (
    !canMoveCandidateToStage(fromType, targetStage.stageType, {
      sameStageId: candidate.pipelineStageId === targetStage.id,
      fromPosition,
      toPosition: targetStage.position,
    })
  ) {
    return { kind: 'invalid' };
  }

  switch (targetStage.stageType) {
    case 'interviewing':
      return { kind: 'send_interview' };
    case 'accepted':
      return { kind: 'accept' };
    case 'rejected':
      return { kind: 'reject' };
    case 'hired':
      return { kind: 'hire' };
    default:
      return { kind: 'pipeline_move' };
  }
}
